import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Table, Button, Modal, Form, Alert, Spinner, Badge } from 'react-bootstrap';
import { buyerAPI } from '../utils/api';

const BuyerManagement = () => {
  // Data states
  const [buyers, setBuyers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Modal states
  const [showBuyerModal, setShowBuyerModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [selectedBuyer, setSelectedBuyer] = useState(null);
  const [processing, setProcessing] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    taxId: '',
    isActive: true
  });

  // Ref to prevent duplicate loads
  const hasLoadedRef = useRef(false);

  // Load buyers on mount
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadBuyers();
    }
  }, []);

  const loadBuyers = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await buyerAPI.getAll();
      setBuyers(res.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'เกิดข้อผิดพลาดในการโหลดข้อมูล');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClick = () => {
    setModalMode('create');
    setSelectedBuyer(null);
    setFormData({
      name: '',
      address: '',
      taxId: '',
      isActive: true
    });
    setShowBuyerModal(true);
  };

  const handleEditClick = (buyer) => {
    setModalMode('edit');
    setSelectedBuyer(buyer);
    setFormData({
      name: buyer.name,
      address: buyer.address || '',
      taxId: buyer.taxId || '',
      isActive: buyer.isActive
    });
    setShowBuyerModal(true);
  };

  const handleCloseModal = () => {
    setShowBuyerModal(false);
    setFormData({
      name: '',
      address: '',
      taxId: '',
      isActive: true
    });
    setSelectedBuyer(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setProcessing(true);
    setError('');

    try {
      if (modalMode === 'create') {
        await buyerAPI.create(formData);
        setSuccess('สร้างผู้ซื้อสำเร็จ');
      } else {
        await buyerAPI.update(selectedBuyer.id, formData);
        setSuccess('อัปเดตข้อมูลผู้ซื้อสำเร็จ');
      }
      handleCloseModal();
      loadBuyers();
    } catch (err) {
      setError(err.response?.data?.message || `เกิดข้อผิดพลาดในการ${modalMode === 'create' ? 'สร้าง' : 'อัปเดตข้อมูล'}ผู้ซื้อ`);
    } finally {
      setProcessing(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`ต้องการลบผู้ซื้อ "${name}" หรือไม่?`)) {
      return;
    }

    try {
      setError('');
      await buyerAPI.delete(id);
      setSuccess('ลบผู้ซื้อสำเร็จ');
      loadBuyers();
    } catch (err) {
      setError(err.response?.data?.message || 'เกิดข้อผิดพลาดในการลบผู้ซื้อ');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  return (
    <Container fluid className="main-content">
      <Row className="mb-4">
        <Col>
          <h2>
            <i className="bi bi-person-check me-2"></i>
            จัดการผู้ซื้อ
          </h2>
          <p className="text-muted">จัดการข้อมูลผู้ซื้อสินค้า</p>
        </Col>
        <Col xs="auto">
          <Button variant="primary" onClick={handleCreateClick}>
            <i className="bi bi-plus-circle me-2"></i>
            เพิ่มผู้ซื้อใหม่
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Card>
        <Card.Header>
          <h5 className="mb-0">รายการผู้ซื้อ</h5>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 text-muted">กำลังโหลดข้อมูล...</p>
            </div>
          ) : buyers.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-inbox display-1 text-muted"></i>
              <p className="text-muted mt-3">ไม่มีข้อมูลผู้ซื้อ</p>
            </div>
          ) : (
            <Table responsive hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>ID</th>
                  <th>ชื่อ</th>
                  <th>ที่อยู่</th>
                  <th>เลขประจำตัวผู้เสียภาษี</th>
                  <th className="text-center">สถานะ</th>
                  <th className="text-center">การดำเนินการ</th>
                </tr>
              </thead>
              <tbody>
                {buyers.map((buyer) => (
                  <tr key={buyer.id}>
                    <td>{buyer.id}</td>
                    <td><strong>{buyer.name}</strong></td>
                    <td>{buyer.address || '-'}</td>
                    <td>{buyer.taxId || '-'}</td>
                    <td className="text-center">
                      {buyer.isActive ? (
                        <Badge bg="success">ใช้งาน</Badge>
                      ) : (
                        <Badge bg="secondary">ไม่ใช้งาน</Badge>
                      )}
                    </td>
                    <td className="text-center">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="me-2"
                        onClick={() => handleEditClick(buyer)}
                      >
                        <i className="bi bi-pencil"></i> แก้ไข
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDelete(buyer.id, buyer.name)}
                      >
                        <i className="bi bi-trash"></i> ลบ
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Create/Edit Modal */}
      <Modal show={showBuyerModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {modalMode === 'create' ? 'เพิ่มผู้ซื้อใหม่' : 'แก้ไขข้อมูลผู้ซื้อ'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>ชื่อผู้ซื้อ <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="กรอกชื่อผู้ซื้อ"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>เลขประจำตัวผู้เสียภาษี</Form.Label>
                  <Form.Control
                    type="text"
                    name="taxId"
                    value={formData.taxId}
                    onChange={handleInputChange}
                    placeholder="กรอกเลขประจำตัวผู้เสียภาษี"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>ที่อยู่</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="กรอกที่อยู่"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    name="isActive"
                    label="ใช้งาน"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal} disabled={processing}>
              ยกเลิก
            </Button>
            <Button variant="primary" type="submit" disabled={processing}>
              {processing ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  กำลังบันทึก...
                </>
              ) : (
                'บันทึก'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default BuyerManagement;
